/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartPie,
  HiOutlineOfficeBuilding,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import { Avatar } from "../../ui";
import type { Action, Column } from "../../ui";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useCompanyContext } from "../../context/CompanyContext";
import { pushOneCompany } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type CompanyRow = {
  company_id: number | string;
  name?: string;
  website?: string;
  owner_id?: number | string;
  [key: string]: unknown;
};

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin } = useAdminContext();
  const role = (sysAdmin as any)?.role;
  const canAdd = ["admin", "super_admin"].includes(role);

  const { companies, count, getAll, deleteCompany, setCompanies, setCompany } =
    useCompanyContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!companies) getAll(page, pageSize);
  }, [companies]);

  const rows = useMemo<CompanyRow[] | null>(() => {
    if (!Array.isArray(companies)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return companies as CompanyRow[];
    return (companies as CompanyRow[]).filter((c) =>
      String(c.name ?? "").toLowerCase().includes(q),
    );
  }, [companies, query]);

  const handleDelete = async (row: CompanyRow) => {
    const { status } = await deleteCompany(row.company_id, row.owner_id);
    if (status === 200 && Array.isArray(companies)) {
      setCompanies(
        (companies as CompanyRow[]).filter(
          (c) => c.company_id !== row.company_id,
        ),
      );
    }
  };

  const columns: Column<CompanyRow>[] = [
    {
      header: "Company",
      cell: (row) => {
        const letters = (row.name ?? "CO").slice(0, 2).toUpperCase();
        return (
          <div className="flex items-center gap-3">
            <Avatar
              firstName={letters[0]}
              lastName={letters[1]}
              size="sm"
              tone="neutral"
            />
            <div className="min-w-0">
              <p className="truncate font-medium text-gray-900">
                {row.name ?? "—"}
              </p>
              <p className="truncate text-[11px] text-gray-500">
                ID: {row.company_id}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      header: "Website",
      cell: (row) =>
        row.website ? (
          <span className="inline-flex max-w-[440px] truncate rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 ring-1 ring-gray-200">
            {row.website}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  const rowActions = (row: CompanyRow): Action[] => [
    {
      label: "Dashboard",
      icon: <HiOutlineChartPie className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneCompany(row);
        navigate("/company/dashboard");
      },
    },
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneCompany(row);
        setCompany(null);
        navigate("/company/edit");
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name ?? "this company"}?`,
        description: "The company and its associated data will be removed.",
        confirmLabel: "Delete",
      },
    },
  ];

  const pageActions: Action[] = canAdd
    ? [{ label: "Add company", variant: "primary", href: "/company/add" }]
    : [];

  return (
    <ListPage<CompanyRow>
      title="Companies"
      subtitle="Partner companies managed through the dashboard."
      icon={<HiOutlineOfficeBuilding className="h-5 w-5" />}
      actions={pageActions}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.company_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search companies by name…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          getAll(p, ps);
        },
      }}
      empty="No companies yet."
    />
  );
};

export default Overview;
